import { useCharacterCommissions, useCommission } from '@sorrem-client/ui/hooks';
import type { CrucibleRecord } from '@sorrem/shared';
import { useCallback, useEffect, useRef } from 'react';

export interface DirectEntryState {
    /**
     * Whether this Crucible was provisioned by an accepted direct-entry
     * commission, so the gathering screen should enter on the player's behalf
     * instead of waiting for them to press Enter.
     */
    isDirectEntry: boolean;
    /** True until the character's commissions (and the bound one's content) have loaded; nothing auto-enters yet. */
    isLoading: boolean;
    /** The server had no room for the run (#214): asks again. A party run retries by readying up instead. */
    retry: () => void;
}

interface DirectEntryInput {
    readonly crucible: CrucibleRecord | null;
    /** Whether the viewer created the crucible; only they can start it. */
    readonly isCreator: boolean;
    /** Whether the run may start now (its before-cutscene is done with). */
    readonly canStart: boolean;
    readonly startCrucible: () => Promise<void>;
}

/**
 * Whether the player is here because they accepted a direct-entry commission,
 * and entering for them when they are.
 *
 * Resolved from the character's own commissions rather than from navigation
 * state, so reloading the page mid-entry resolves the same way instead of
 * stranding them in a lobby they were never meant to see.
 */
export function useDirectEntry(crucibleId: string | null, { crucible, isCreator, canStart, startCrucible }: DirectEntryInput): DirectEntryState {
    const { commissions, isLoading: isCommissionsLoading } = useCharacterCommissions();
    const bound = crucibleId === null
        ? null
        : commissions.find((commission) => commission.status === 'accepted' && commission.crucibleId === crucibleId) ?? null;
    const { commission: content, isLoading: isContentLoading } = useCommission(bound?.commissionId ?? null);
    const isDirectEntry = crucibleId !== null && content?.hasDirectEntry === true;
    const isLoading = crucibleId !== null && (isCommissionsLoading || isContentLoading);

    /*
     * Direct entry: the player accepted a commission that provisioned this
     * Crucible for them alone, so there is nobody to gather for. Enter as soon
     * as the party channel has the crucible, rather than showing them a lobby
     * and an Enter button they'd have to press to get on with it. The ref
     * makes this once-per-mount: startCrucible is rejected server-side for an
     * already-started run, and a re-render mid-start must not retry it.
     * Waits for the run's before-cutscene, so the story is told before the
     * countdown rather than over it.
     */
    const hasRequestedRef = useRef(false);
    useEffect(() => {
        if (!isDirectEntry || isLoading || crucible === null || !isCreator || !canStart) {
            return;
        }
        if (crucible.startedAt !== null || hasRequestedRef.current) {
            return;
        }

        hasRequestedRef.current = true;
        void startCrucible();
    }, [canStart, crucible, isCreator, isDirectEntry, isLoading, startCrucible]);

    const retry = useCallback((): void => {
        void startCrucible();
    }, [startCrucible]);

    return { isDirectEntry, isLoading, retry };
}
